import { BestAndWorstGraphGlobal, InvestmentGrowthGraphGlobal, TimeToRecoverGraphGlobal } from "./main";
import { Point, drawGraph } from "@rmlibrary/graph";
import {
    formatLargeCurrency,
    getLanguage,
    getMonthAndYearFromTimestamp,
    getTextWidth,
    getTimestampFromDate,
    getYearFromTimestamp,
    pluralizeText,
    roundToNiceNumber
} from "./utilities";

import $ from "jquery";
import Formatter from "@rmlibrary/formatting";

const isIE = /Trident\/7\./.test(navigator.userAgent) || navigator.userAgent.indexOf("MSIE ") >= 0;

const grey = "#666";
const white = "#ffffff";
const monthsInAYear = 12;
const lang = getLanguage();
const formatter = new Formatter(lang);

function getBestAndWorstGraphCSS({ investmentAmount, screenPixelRatio, screenWidth }) {
    function getYAxisValueWidth(font) {
        let yAxisValue = formatLargeCurrency(investmentAmount);
        if (screenWidth === "regular") {
            yAxisValue = formatter.formatCurrency(investmentAmount, 0);
        }

        return getTextWidth(yAxisValue, font);
    }

    const title = {
        colour: "#000000",
        font: "Roobert",
        rotateYLabel: true,
        size: "0.8rem",
        weight: "bold",
        xLabelTextAlign: "center",
        xLabelTextVerticalAlign: "center",
        yLabelTextAlign: "center",
        yLabelTextHorizontalAlign: "center"
    };
    const numbers = {
        xFormat: () => "",
        offsetValuesForAccTable: (number) => number + investmentAmount,
        yFormat: (number) => {
            if (screenWidth === "regular") {
                number = formatter.formatCurrency(number, 0);
            } else {
                number = formatLargeCurrency(number);
            }

            return number;
        },
        formatYValuesForAccTable: (number) => formatter.formatCurrency(number, 0),
        font: "Roobert",
        size: 15 * screenPixelRatio,
        padding: {
            xAxis: 10 * screenPixelRatio,
            yAxis: 10 * screenPixelRatio
        },
        xAxisWeight: "normal",
        yAxisWeight: "normal",
        colour: "#757575"
    };
    const axisCss = {
        axisLineWidth: 1 * screenPixelRatio,
        axisLineColour: "#4A4A4A",
        gridLineWidth: 1 * screenPixelRatio,
        gridLineColour: "#4A4A4A",
        title,
        numbers
    };

    const numbersFont = `${numbers.size}px ${numbers.font}`;
    const left = getYAxisValueWidth(numbersFont) + numbers.padding.xAxis;
    const top = screenWidth === "regular" ? 35 : 90;
    const padding = {
        left,
        right: 0,
        top: top * screenPixelRatio,
        bottom: 35 * screenPixelRatio
    };

    const canvasCss = { padding, colour: "white" };

    const titleCss = {
        font: "Roobert",
        weight: "bold",
        size: 20 * screenPixelRatio,
        colour: "rgba(0, 0, 0, 0)",
        textAlign: "center"
    };

    return {
        axisCss,
        canvasCss,
        titleCss
    };
}

function getBestAndWorstGraphDataset({
    assetMixOne,
    assetMixTwo,
    drawCallback,
    barColourOne,
    barColourTwo,
    barColourThree,
    barColourFour,
    barImageOne,
    barImageTwo,
    barImageThree,
    barImageFour,
    css: {
        axisCss: { numbers }
    },
    investmentAmount,
    screenPixelRatio,
    screenWidth,
    timePeriod
}) {
    let individualColoursOne = [barColourOne];
    let individualColoursTwo = [barColourTwo];
    let pointsOne = [
        new Point(
            1,
            assetMixOne.bestYear.portfolioValue - investmentAmount,
            drawCallback({ assetMix: assetMixOne, screenPixelRatio, screenWidth, yearType: "best" })
        )
    ];
    let pointsTwo = [
        new Point(
            2,
            assetMixOne.worstYear.portfolioValue - investmentAmount,
            drawCallback({ assetMix: assetMixOne, screenPixelRatio, screenWidth, yearType: "worst" })
        )
    ];

    if (assetMixTwo) {
        individualColoursOne = individualColoursOne.concat(barColourThree);
        individualColoursTwo = individualColoursTwo.concat(barColourFour);
        pointsOne = pointsOne.concat(
            new Point(
                5,
                assetMixTwo.bestYear.portfolioValue - investmentAmount,
                drawCallback({ assetMix: assetMixTwo, screenPixelRatio, screenWidth, yearType: "best" })
            )
        );
        pointsTwo = pointsTwo.concat(
            new Point(
                6,
                assetMixTwo.worstYear.portfolioValue - investmentAmount,
                drawCallback({ assetMix: assetMixTwo, screenPixelRatio, screenWidth, yearType: "worst" })
            )
        );
    }

    const names = {
        bestYear: {
            en: `Best ${timePeriod} year period`,
            fr: `Meilleure période de ${pluralizeText("an", timePeriod)}`
        },
        worstYear: {
            en: `Worst ${timePeriod} year period`,
            fr: `Pire période de ${pluralizeText("an", timePeriod)}`
        }
    };
    let barOneFill;
    let barTwoFill;
    let barThreeFill;
    let barFourFill;
    const barOneCanvas = document.getElementById("barCanvas1");
    const barTwoCanvas = document.getElementById("barCanvas2");
    const barThreeCanvas = document.getElementById("barCanvas3");
    const barFourCanvas = document.getElementById("barCanvas4");
    const barOneContext = barOneCanvas.getContext("2d");
    const barTwoContext = barTwoCanvas.getContext("2d");
    const barThreeContext = barThreeCanvas.getContext("2d");
    const barFourContext = barFourCanvas.getContext("2d");

    const assetMixNames = document.querySelectorAll(".best-worst-label");
    if (barImageOne) {
        barOneFill = barOneContext.createPattern(barImageOne, "repeat");
    } else {
        barOneFill = individualColoursOne[0];
    }
    if (barImageTwo) {
        barTwoFill = barTwoContext.createPattern(barImageTwo, "repeat");
    } else {
        barTwoFill = individualColoursOne[1];
    }
    if (barImageThree) {
        barThreeFill = barThreeContext.createPattern(barImageThree, "repeat");
    } else {
        barThreeFill = individualColoursTwo[0];
    }
    if (barImageFour) {
        barFourFill = barFourContext.createPattern(barImageFour, "repeat");
    } else {
        barFourFill = individualColoursTwo[1];
    }

    const dataset = [
        {
            css: {
                fillColour: barOneFill,
                justify: "none",
                width: 1,
                shadow: {
                    blur: 0,
                    shadowColor: "rgba(0, 0, 0, 0.25)",
                    shadowOffsetX: 0,
                    shadowOffsetY: 0
                }
            },
            name: `${assetMixNames[0].textContent.trim()} - ${names.bestYear[lang]}`,
            points: [pointsOne[0]],
            type: "bar"
        }
    ];

    if (pointsOne.length === 2) {
        dataset.push({
            css: {
                fillColour: barTwoFill,
                justify: "none",
                width: 1,
                shadow: {
                    blur: 0,
                    shadowColor: "rgba(0, 0, 0, 0.25)",
                    shadowOffsetX: 0,
                    shadowOffsetY: 0
                }
            },
            name: `${assetMixNames[1].textContent.trim()} - ${names.bestYear[lang]}`,
            points: [pointsOne[1]],
            type: "bar"
        });
    }

    dataset.push({
        css: {
            fillColour: barThreeFill,
            justify: "none",
            width: 1,
            shadow: {
                blur: 0,
                shadowColor: "rgba(0, 0, 0, 0.25)",
                shadowOffsetX: 0,
                shadowOffsetY: 0
            }
        },
        name: `${assetMixNames[0].textContent.trim()} - ${names.worstYear[lang]}`,
        points: [pointsTwo[0]],
        type: "bar"
    });

    if (pointsOne.length === 2) {
        dataset.push({
            css: {
                fillColour: barFourFill,
                justify: "none",
                width: 1,
                shadow: {
                    blur: 0,
                    shadowColor: "rgba(0, 0, 0, 0.25)",
                    shadowOffsetX: 0,
                    shadowOffsetY: 0
                }
            },
            name: `${assetMixNames[1].textContent.trim()} - ${names.worstYear[lang]}`,
            points: [pointsTwo[1]],
            type: "bar"
        });
    }

    const investmentAmountDrawCallback = (context, _x, y) => {
        const { colour, font, size, yAxisWeight, yFormat } = numbers;
        context.save();
        context.fillStyle = colour;
        context.font = `${yAxisWeight} ${size}px ${font}`;
        context.textAlign = "left";
        context.textBaseline = "middle";
        const value = yFormat(investmentAmount);
        context.fillText(value, 0, y);
        context.restore();
    };
    BestAndWorstGraphGlobal.investmentAmountDrawCallback = investmentAmountDrawCallback;
    dataset.push({
        css: {
            colour: "#4A4A4A",
            width: 1 * screenPixelRatio
        },
        isHorizontal: true,
        name: "Investment Amount",
        points: [new Point(0, 0, investmentAmountDrawCallback)],
        type: "dashedLine"
    });

    if (BestAndWorstGraphGlobal.initialDraw) {
        const oldDataset = JSON.parse(JSON.stringify(dataset));
        oldDataset.forEach((dataset) => {
            if (dataset.type !== "dashedLine") {
                dataset.points.forEach((point) => {
                    dataset.points = [new Point(point.x, 0)];
                });
            }
        });
        BestAndWorstGraphGlobal.oldDataset = oldDataset;
    }

    return dataset;
}

function getBestAndWorstGraphAxesLimits({ assetMixOne, assetMixTwo, investmentAmount, type }) {
    const xAxisMin = 0;
    let xAxisMax = 4;

    let yAxisValues = [
        assetMixOne.bestYear.portfolioValue - investmentAmount,
        assetMixOne.worstYear.portfolioValue - investmentAmount
    ];
    if (assetMixTwo) {
        if (type === "page") {
            xAxisMax = 8;
        }

        yAxisValues = yAxisValues.concat([
            assetMixTwo.bestYear.portfolioValue - investmentAmount,
            assetMixTwo.worstYear.portfolioValue - investmentAmount
        ]);
    }

    const yAxisMax = Math.max(...yAxisValues);
    const yAxisMin = Math.min(...yAxisValues);

    let magnitude = Math.abs(yAxisMax);
    let length = magnitude.toFixed(0).length - 1;
    let multiplier = 10 ** length;
    const yAxisMaxMagnitude = Math.ceil(magnitude / multiplier) * multiplier;

    magnitude = Math.abs(yAxisMin);
    length = magnitude.toFixed(0).length - 1;
    multiplier = 10 ** length;
    const yAxisMinMagnitude = Math.ceil(magnitude / multiplier) * multiplier;

    return {
        xAxis: {
            max: xAxisMax,
            min: xAxisMin
        },
        yAxis: {
            max: yAxisMaxMagnitude,
            min: -yAxisMinMagnitude
        }
    };
}

function getBestAndWorstGraphOptions({
    assetMixOne,
    assetMixTwo,
    canvas,
    investmentAmount,
    screenPixelRatio,
    screenWidth,
    axesLimits,
    popup,
    type,
    updateContent
}) {
    let xAxis = null;
    let yAxis = null;
    if (type === "print" && axesLimits) {
        xAxis = axesLimits.xAxis;
        yAxis = axesLimits.yAxis;
    } else {
        const normalAxesLimits = getBestAndWorstGraphAxesLimits({ assetMixOne, assetMixTwo, investmentAmount, type });
        xAxis = normalAxesLimits.xAxis;
        yAxis = normalAxesLimits.yAxis;
    }

    let axisOptions = {
        xAxis: {
            name: "",
            drawGridLines: false,
            drawAxisLine: false,
            range: [xAxis.min, xAxis.max],
            clusterNames: ["", "", "", "", ""]
        },
        yAxis: {
            name: "",
            drawGridLines: false,
            drawAxisLine: false,
            range: [yAxis.min, yAxis.max],
            ticks: 0
        }
    };

    if (assetMixTwo) {
        axisOptions.xAxis.clusterNames = ["", "", "", "", "", "", "", "", ""];
    }

    function hoverCallback(dataName, x, y, _xPos, _yPos, xValOnGraph, _yValOnGraph) {
        const oldDataset = JSON.parse(JSON.stringify(BestAndWorstGraphGlobal.dataset));
        let index = -1;
        let oldIndex = -1;
        oldDataset.forEach((dataset, i) => {
            if (dataset.points[0].y === y) {
                index = i;
            }
            if (dataset.hovered) {
                oldIndex = i;
            }
        });

        let initialTime = undefined;
        const ease = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);

        if (oldIndex !== -1 && dataName !== oldDataset[oldIndex].name) {
            cancelAnimationFrame(BestAndWorstGraphGlobal.rafId);
            BestAndWorstGraphGlobal.rafId = null;
        }

        oldDataset[index].css.width = Math.round(oldDataset[index].css.width * 10) / 10;

        if (oldDataset[index].css.width !== 1.4 && !BestAndWorstGraphGlobal.rafId) {
            const newDataset = JSON.parse(JSON.stringify(BestAndWorstGraphGlobal.dataset));
            const positionOfWorst = oldDataset.length === 3 ? 1 : 2;
            const initialX = newDataset[index].points[0].x;

            newDataset.forEach((dataset, i) => {
                const name = dataset.name.toLowerCase();
                if (dataset.type !== "dashedLine" && Math.round(dataset.css.width * 10) / 10 === 1.4) {
                    if (name.indexOf("best") > -1 || name.indexOf("meilleure") > -1) {
                        dataset.points[0].x += newDataset[i + positionOfWorst].points[0].y > 0 ? 0.4 : 0.2;
                    } else {
                        dataset.points[0].x += dataset.points[0].y > 0 ? 0 : 0.2;
                    }
                    dataset.css.width = 1;
                    dataset.css.shadow = {
                        blur: 0,
                        shadowColor: "rgba(0, 0, 0, 0.6)",
                        shadowOffsetX: 0,
                        shadowOffsetY: 0
                    };

                    if (dataset.hovered) {
                        dataset.hovered = undefined;
                    }
                    BestAndWorstGraphGlobal.dataset[i] = dataset;
                }
            });

            const animateBar = function (time) {
                if (!initialTime) initialTime = time;
                const elapsed = time - initialTime;

                let xDisplacement = newDataset[index].points[0].y > 0 ? 0 : 0.2;

                if (dataName.indexOf("Best") > -1 || dataName.indexOf("Meilleure") > -1) {
                    xDisplacement = newDataset[index + positionOfWorst].points[0].y > 0 ? 0.4 : 0.2;
                }

                const t = ease(elapsed / 200);
                newDataset[index].points[0].x = Math.max(initialX - t * xDisplacement, initialX - xDisplacement);

                newDataset[index].css.width = Math.min(1 + t * 0.4, 1.4);
                newDataset[index].css.shadow = {
                    blur: Math.min(t * 10, 10),
                    shadowColor: "rgba(0, 0, 0, 0.6)",
                    shadowOffsetX: 0,
                    shadowOffsetY: 0
                };

                for (let i = 0; i < newDataset.length; i++) {
                    const point = newDataset[i].points[0];
                    const name = newDataset[i].name.toLowerCase();
                    let isHovered = i === index ? true : false;
                    let assetMix = assetMixOne;

                    if (oldDataset.length > 3 && (i === 1 || i === 3)) {
                        assetMix = assetMixTwo;
                    }

                    const drawCallback =
                        newDataset[i].type === "dashedLine"
                            ? BestAndWorstGraphGlobal.investmentAmountDrawCallback
                            : BestAndWorstGraphGlobal.drawCallback({
                                  assetMix,
                                  isHovered,
                                  screenPixelRatio,
                                  screenWidth,
                                  yearType:
                                      name.indexOf("best") > -1 || name.indexOf("meilleure") > -1 ? "best" : "worst"
                              });
                    newDataset[i].points[0] = new Point(point.x, point.y, drawCallback);
                }

                BestAndWorstGraphGlobal.options.accessibilityTable = false;
                canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
                drawGraph(canvas, BestAndWorstGraphGlobal.options, BestAndWorstGraphGlobal.css, newDataset);
                BestAndWorstGraphGlobal.options.accessibilityTable = true;

                if (elapsed < 200 && newDataset[index].css.width !== 1.4) {
                    BestAndWorstGraphGlobal.rafId = requestAnimationFrame(animateBar);
                } else {
                    newDataset[index].points[0].x = initialX - xDisplacement;
                    newDataset[index].css.width = 1.4;
                    newDataset[index].hovered = true;
                    BestAndWorstGraphGlobal.dataset = newDataset;
                    BestAndWorstGraphGlobal.rafId = null;
                }
            };

            BestAndWorstGraphGlobal.rafId = requestAnimationFrame(animateBar);

            updateContent({ dataName, popup, x, y });

            const xOffset = $(popup).outerWidth() / 2;

            const point = oldDataset[index].points[0];
            const width = window.outerWidth;

            let barWidth = Math.abs(point.coords.x2 - point.coords.x1);
            let leftPosition = xValOnGraph - xOffset + barWidth / 2;
            let topPosition;
            setTimeout(() => {
                let offset;
                if (width <= 450) {
                    offset = lang === "en" ? 100 : 120;
                } else if (width <= 575) {
                    offset = lang === "en" ? 125 : 140;
                } else if (width <= 767) {
                    offset = lang === "en" ? 125 : 130;
                } else if (width <= 1399) {
                    offset = lang === "en" ? 125 : 130;
                } else {
                    offset = lang === "en" ? 130 : 130;
                }
                topPosition = point.coords.y2 - offset - 25;
                const onlyOneAssetMix = assetMixOne && !assetMixTwo;

                if (onlyOneAssetMix) {
                    const bothPositiveBars =
                        assetMixOne.bestYear.portfolioValue >= investmentAmount &&
                        assetMixOne.worstYear.portfolioValue >= investmentAmount;
                    const barPopupPadding = bothPositiveBars ? 42 : 26;

                    if (x === 1) {
                        $(popup).attr("class", "popup-left");
                        leftPosition = xValOnGraph - $(popup).outerWidth() - barPopupPadding;
                    } else {
                        $(popup).attr("class", "popup-top");
                        leftPosition = xValOnGraph + barPopupPadding;
                    }
                } else {
                    if (x === 1 || x === 5) {
                        $(popup).attr("class", "popup-left");
                        leftPosition = xValOnGraph - $(popup).outerWidth() - 17;
                    } else if (x === 2 || x === 6) {
                        $(popup).attr("class", "popup-top");
                        leftPosition = xValOnGraph + barWidth / 3;
                    }
                }

                $(popup).css("left", `${leftPosition}px`);
                $(popup).css("top", `${topPosition}px`);
                if ($(popup).css("visibility") !== "visible") {
                    $(popup).css("visibility", "visible");
                }
            }, 200);
        }
    }

    function hoverOffCallback() {
        const oldDataset = JSON.parse(JSON.stringify(BestAndWorstGraphGlobal.dataset));
        let index = -1;

        oldDataset.forEach((dataset, i) => {
            if (dataset.css.width !== 1) {
                index = i;
            }
        });

        let initialTime = undefined;
        const ease = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);

        const hoveredSet = index !== -1 ? oldDataset[index] : undefined;

        if (hoveredSet && !BestAndWorstGraphGlobal.rafId) {
            const initialX = hoveredSet.points[0].x;
            const positionOfWorst = oldDataset.length === 3 ? 1 : 2;

            const animateBar = function (time) {
                if (!initialTime) initialTime = time;
                const elapsed = time - initialTime;
                const dataName = oldDataset[index].name;
                let xDisplacement = oldDataset[index].points[0].y > 0 ? 0 : 0.2;

                if (dataName.indexOf("Best") > -1 || dataName.indexOf("Meilleure") > -1) {
                    xDisplacement = oldDataset[index + positionOfWorst].points[0].y > 0 ? 0.4 : 0.2;
                }

                const t = ease(elapsed / 200);
                oldDataset[index].points[0].x = Math.min(initialX + t * xDisplacement, initialX + xDisplacement);

                oldDataset[index].css.width = Math.max(1.4 - t * 0.4, 1);
                oldDataset[index].css.shadow = {
                    blur: Math.max((1 - t) * 10, 0),
                    shadowColor: "rgba(0, 0, 0, 0.6)",
                    shadowOffsetX: 0,
                    shadowOffsetY: 0
                };

                for (let i = 0; i < oldDataset.length; i++) {
                    const point = oldDataset[i].points[0];
                    const name = oldDataset[i].name.toLowerCase();
                    const isHovered = false;
                    let assetMix = assetMixOne;

                    if (oldDataset.length > 3 && (i === 1 || i === 3)) {
                        assetMix = assetMixTwo;
                    }

                    const drawCallback =
                        oldDataset[i].type === "dashedLine"
                            ? BestAndWorstGraphGlobal.investmentAmountDrawCallback
                            : BestAndWorstGraphGlobal.drawCallback({
                                  assetMix,
                                  isHovered,
                                  screenPixelRatio,
                                  screenWidth,
                                  yearType:
                                      name.indexOf("best") > -1 || name.indexOf("meilleure") > -1 ? "best" : "worst"
                              });
                    oldDataset[i].points[0] = new Point(point.x, point.y, drawCallback);
                }

                BestAndWorstGraphGlobal.options.accessibilityTable = false;
                canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
                drawGraph(canvas, BestAndWorstGraphGlobal.options, BestAndWorstGraphGlobal.css, oldDataset);
                BestAndWorstGraphGlobal.options.accessibilityTable = true;

                if (elapsed < 200 && oldDataset[index].css.width !== 1) {
                    BestAndWorstGraphGlobal.rafId = requestAnimationFrame(animateBar);
                } else {
                    oldDataset[index].css.width = 1;
                    oldDataset[index].points[0].x = initialX + xDisplacement;
                    oldDataset[index].hovered = undefined;
                    BestAndWorstGraphGlobal.dataset = oldDataset;
                    BestAndWorstGraphGlobal.rafId = null;
                }
            };

            BestAndWorstGraphGlobal.rafId = requestAnimationFrame(animateBar);
            if ($(popup).css("visibility") === "visible") {
                $(popup).css("visibility", "hidden");
            }
        }
    }

    const events =
        type === "page"
            ? {
                  hoverOn: hoverCallback,
                  hoverOff: hoverOffCallback,
                  clickOn: hoverCallback,
                  clickOff: hoverOffCallback
              }
            : null;

    const titles = {
        en: "Best and Worst Years",
        fr: "Meilleures et pires années"
    };

    return {
        accessibilityTable: false,
        autoResize: false,
        axisOptions,
        events,
        pixelRatio: screenPixelRatio,
        title: titles[lang].split(" ").join("-")
    };
}

function getInvestmentGrowthGraphCSS({ screenPixelRatio, screenWidth }) {
    const title = {
        font: "Roobert",
        weight: "bold",
        size: "0.8rem",
        colour: "rgba(0, 0, 0, 0)",
        rotateYLabel: true,
        xLabelTextAlign: "center",
        xLabelTextVerticalAlign: "center",
        yLabelTextAlign: "center",
        yLabelTextHorizontalAlign: "center"
    };
    const numbers = {
        xFormat: (number) => `${getYearFromTimestamp(number)}`,
        xFormatForAccTable: (number) => `${getMonthAndYearFromTimestamp(number)}`,
        yFormat: (number) => {
            if (screenWidth === "regular") {
                number = formatter.formatCurrency(number, 0);
            } else {
                number = formatLargeCurrency(number);
            }

            return number;
        },
        formatYValuesForAccTable: (number) => formatter.formatCurrency(number, 0),
        font: "Roobert",
        size: 15 * screenPixelRatio,
        padding: {
            xAxis: 10 * screenPixelRatio,
            yAxis: 10 * screenPixelRatio
        },
        xAxisWeight: "normal",
        yAxisWeight: "normal",
        colour: grey
    };
    const axisCss = {
        axisLineWidth: 1 * screenPixelRatio,
        axisLineColour: grey,
        gridLineWidth: 1 * screenPixelRatio,
        gridLineColour: grey,
        title,
        numbers
    };

    const left = screenWidth === "regular" ? 118 : 65;
    const padding = {
        left: left * screenPixelRatio,
        right: 20 * screenPixelRatio,
        top: 20 * screenPixelRatio,
        bottom: 40 * screenPixelRatio
    };
    const canvasCss = { padding, colour: "white" };

    const titleCss = {
        font: "Roobert",
        weight: "bold",
        size: 20 * screenPixelRatio,
        colour: "rgba(0, 0, 0, 0)",
        textAlign: "center"
    };

    return {
        axisCss,
        canvasCss,
        titleCss
    };
}

function getInvestmentGrowthGraphDataset({
    assetMixOne,
    assetMixTwo,
    canvas,
    drawCallback,
    lineColourOne,
    lineColourTwo,
    page,
    screenPixelRatio,
    showPoints
}) {
    function createPoint(lineColour, isDashedLine) {
        return (data) => {
            let { date, portfolioValue } = data;
            date = getTimestampFromDate(date);

            return new Point(
                date,
                isDashedLine ? 0 : portfolioValue,
                isDashedLine
                    ? undefined
                    : drawCallback({
                          ...data,
                          lineColour,
                          portfolioValue,
                          screenPixelRatio
                      })
            );
        };
    }

    const assetMixNames = document.querySelectorAll(".best-worst-label");
    const datesForDashedLines = ["06/1990", "06/2000", "06/2010", "06/2020"];

    const pointsForDashedLines = assetMixOne
        .filter((mix) => datesForDashedLines.indexOf(mix.date) !== -1)
        .map(createPoint(grey, true));

    const pointsSize = 6 * screenPixelRatio;
    let dataset = [
        {
            css: {
                width: 4 * screenPixelRatio,
                colour: lineColourOne,
                points: {
                    colour: white,
                    showPoints: showPoints(assetMixOne),
                    size: pointsSize,
                    style: "circle"
                },
                shadow: {
                    blur: 0,
                    shadowColor: "rgba(0, 0, 0, 0.25)",
                    shadowOffsetX: 0,
                    shadowOffsetY: 0
                }
            },
            points: assetMixOne.map(createPoint(lineColourOne)),
            name: assetMixNames[0].innerText,
            type: "line",
            enableLineHover: true,
            animate: InvestmentGrowthGraphGlobal.previousColours.indexOf(lineColourOne) === -1 // If initialTimeout !== 0 means the graph has never been drawn before
        }
    ];

    // Removes points that are too close together and merges them into one storing the indices of both
    if (assetMixTwo) {
        dataset = dataset.concat({
            css: {
                width: 4 * screenPixelRatio,
                colour: lineColourTwo,
                points: {
                    colour: white,
                    showPoints: showPoints(assetMixTwo),
                    size: pointsSize,
                    style: "circle"
                },
                shadow: {
                    blur: 0,
                    shadowColor: "rgba(0, 0, 0, 0.25)",
                    shadowOffsetX: 0,
                    shadowOffsetY: 0
                }
            },
            points: assetMixTwo.map(createPoint(lineColourTwo)),
            name: assetMixNames[1].innerText,
            type: "line",
            enableLineHover: true,
            animate: InvestmentGrowthGraphGlobal.previousColours.indexOf(lineColourTwo) === -1
        });

        if (page) {
            const { yAxis } = getInvestmentGrowthGraphAxesLimits({
                assetMixOne,
                assetMixTwo,
                canvas,
                css: InvestmentGrowthGraphGlobal.css,
                ticks: {
                    yAxis: 8,
                    xAxis: 14
                }
            });

            const xAxisLength =
                canvas.width -
                (InvestmentGrowthGraphGlobal.css.canvasCss.padding.left / screenPixelRatio) * devicePixelRatio -
                (InvestmentGrowthGraphGlobal.css.canvasCss.padding.right / screenPixelRatio) * devicePixelRatio;
            const yAxisLength =
                canvas.height -
                (InvestmentGrowthGraphGlobal.css.canvasCss.padding.top / screenPixelRatio) * devicePixelRatio -
                (InvestmentGrowthGraphGlobal.css.canvasCss.padding.bottom / screenPixelRatio) * devicePixelRatio;

            const yAxisScale = yAxisLength / (yAxis.max - yAxis.min);
            const xAxisScale = xAxisLength / dataset[0].points.length;

            const mixOneIndices = showPoints(assetMixOne);
            const mixTwoIndices = showPoints(assetMixTwo);

            const mixOnePoints = dataset[0].points;
            const mixTwoPoints = dataset[1].points;

            const mixOnePointSize = dataset[0].css.points.size + 2 * devicePixelRatio;
            const mixTwoPointSize = dataset[1].css.points.size + 2 * devicePixelRatio;

            const distanceBetweenPoints =
                ((mixOnePointSize + mixTwoPointSize) / 2) * ($(window).outerWidth() < 576 ? 0.88 : 0.8);

            const mergedPoints = [];
            const stackedPoints = [];

            for (let i = 0; i < mixOneIndices.length; i++) {
                for (let j = 0; j < mixTwoIndices.length; j++) {
                    const xValueOne = mixOneIndices[i] * xAxisScale;
                    const xValueTwo = mixTwoIndices[j] * xAxisScale;

                    const yValueOne = mixOnePoints[mixOneIndices[i]].y * yAxisScale;
                    const yValueTwo = mixTwoPoints[mixTwoIndices[j]].y * yAxisScale;

                    if (
                        Math.abs(xValueTwo - xValueOne) < distanceBetweenPoints &&
                        Math.abs(yValueTwo - yValueOne) < distanceBetweenPoints
                    ) {
                        const stackedPointsObject = [];

                        if (mergedPoints.indexOf(mixOneIndices[i]) === -1) {
                            mergedPoints.push(mixOneIndices[i]);
                            stackedPointsObject.push({
                                x: mixOnePoints[mixOneIndices[i]].x,
                                y: mixOnePoints[mixOneIndices[i]].y,
                                name: dataset[0].name
                            });
                        }

                        if (mergedPoints.indexOf(mixTwoIndices[j]) === -1) {
                            mergedPoints.push(mixTwoIndices[j]);
                            stackedPointsObject.push({
                                x: mixTwoPoints[mixTwoIndices[j]].x,
                                y: mixTwoPoints[mixTwoIndices[j]].y,
                                name: dataset[1].name
                            });
                        }

                        if (stackedPointsObject.length !== 0) {
                            stackedPoints.push(stackedPointsObject);
                        }
                    }
                }
            }

            if (mergedPoints.length !== 0) {
                dataset[0].css.points.showPoints = mixOneIndices.filter((i) => mergedPoints.indexOf(i) === -1);
            }

            InvestmentGrowthGraphGlobal.stackedPoints = stackedPoints;
        }
    }

    dataset = dataset.concat(
        pointsForDashedLines.map((dashedLinePoint, i) => {
            return {
                points: [dashedLinePoint],
                name: "Dashed-Line" + (i + 1),
                css: {
                    colour: grey,
                    width: 1 * screenPixelRatio,
                    shadow: {
                        blur: 0,
                        shadowColor: "rgba(0, 0, 0, 0.25)",
                        shadowOffsetX: 0,
                        shadowOffsetY: 0
                    }
                },
                type: "dashedLine",
                isHorizontal: false,
                dashLength: 7 * screenPixelRatio,
                dashSpaceLength: 5 * screenPixelRatio,
                enableLineHover: true,
                animate: false
            };
        })
    );

    if (InvestmentGrowthGraphGlobal.initialDraw) {
        const oldDataset = JSON.parse(JSON.stringify(dataset));
        oldDataset.forEach((dataset) => {
            if (dataset.animate) {
                dataset.points.forEach((point, i) => {
                    dataset.points[i] = new Point(0, 0);
                });
            }
        });
        InvestmentGrowthGraphGlobal.oldDataset = oldDataset;
    }

    return dataset;
}

function getInvestmentGrowthGraphAxesLimits({
    assetMixOne,
    assetMixTwo,
    canvas,
    css: {
        canvasCss: {
            padding: { top, bottom }
        }
    },
    screenPixelRatio,
    ticks
}) {
    function isLegendOverlappingLines({ yAxisMax }) {
        const legendTextFontSize = 15 * screenPixelRatio;
        const { portfolioValue: assetMixOnePortfolioValue } = assetMixOne.slice(-1)[0];

        let maxPortfolioValue = assetMixOnePortfolioValue;
        let legendBottomYOffset = legendTextFontSize;
        if (assetMixTwo) {
            const { portfolioValue: assetMixTwoPortfolioValue } = assetMixTwo.slice(-1)[0];
            maxPortfolioValue = Math.max(maxPortfolioValue, assetMixTwoPortfolioValue);
            legendBottomYOffset = legendBottomYOffset * 2 + 10 * screenPixelRatio;
        }

        const maxPortfolioValueYCoords = (maxPortfolioValue / yAxisMax) * (canvas.height - top - bottom + 10) + bottom;
        const legendBottomYCoords = canvas.height - legendBottomYOffset;

        return legendBottomYCoords < maxPortfolioValueYCoords;
    }

    const xAxisMin = assetMixOne.reduce(
        (min, { date }) => Math.min(min, getTimestampFromDate(date)),
        Number.POSITIVE_INFINITY
    );
    const xAxisMax = assetMixOne.reduce(
        (max, { date }) => Math.max(max, getTimestampFromDate(date)),
        Number.NEGATIVE_INFINITY
    );

    const yAxisMin = 0;
    let yAxisMax = assetMixOne.reduce(
        (max, { portfolioValue }) => Math.max(max, portfolioValue),
        Number.NEGATIVE_INFINITY
    );

    if (assetMixTwo) {
        const yAxisMaxTwo = assetMixTwo.reduce(
            (max, { portfolioValue }) => Math.max(max, portfolioValue),
            Number.NEGATIVE_INFINITY
        );
        yAxisMax = Math.max(yAxisMax, yAxisMaxTwo);
    }

    yAxisMax = roundToNiceNumber(yAxisMax, ticks.yAxis - 1);

    if (isLegendOverlappingLines({ yAxisMax })) {
        yAxisMax = roundToNiceNumber(yAxisMax + 1, ticks.yAxis - 1);
    }

    return {
        xAxis: {
            max: xAxisMax,
            min: xAxisMin
        },
        yAxis: {
            max: yAxisMax,
            min: yAxisMin
        }
    };
}

function getInvestmentGrowthGraphOptions({
    assetMixOne,
    assetMixTwo,
    canvas,
    css,
    screenPixelRatio,
    screenWidth,
    updateContent,
    popupOne,
    popupDashedLine,
    popupStacked
}) {
    function createPoint(lineColour, isHovered, isDashedLine) {
        return (data) => {
            let { date, portfolioValue } = data;
            date = getTimestampFromDate(date);

            return new Point(
                date,
                isDashedLine ? 0 : portfolioValue,
                isDashedLine
                    ? undefined
                    : InvestmentGrowthGraphGlobal.drawCallback({
                          ...data,
                          isHovered,
                          lineColour,
                          portfolioValue,
                          screenPixelRatio
                      })
            );
        };
    }

    const labels = document.querySelectorAll(".best-worst-label");
    const firstAssetMixName = labels[0].innerText;
    let secondAssetMixName = undefined;
    if (labels[1]) {
        secondAssetMixName = labels[1].innerText;
    }

    function hover(dataset, dataName, both = false) {
        if (InvestmentGrowthGraphGlobal.rafId && !both) {
            return;
        }

        if (dataset[1].type === "line" && dataset[0].name === dataName) {
            dataset = [dataset[1], dataset[0], ...dataset.slice(2)];
        }

        if (!both) {
            if (dataset.length === 6) {
                if (dataset[1].css.width === 6 * screenPixelRatio) {
                    return;
                }
            } else {
                if (dataset[0].css.width === 6 * screenPixelRatio) {
                    return;
                }
            }
        } else {
            if (dataset[0].css.width === 6 * screenPixelRatio && dataset[1].css.width === 6 * screenPixelRatio) {
                return;
            }
        }
        let initialTime = null;

        const hoverLine = (time) => {
            if (!initialTime) initialTime = time;
            const elapsed = (time - initialTime) / 200;

            let assetMix = dataset[0].name === firstAssetMixName ? assetMixOne : assetMixTwo;
            let colour = dataset[0].css.colour;

            if (dataset.length === 6) {
                if (both) {
                    // if (dataset[0].css.width !== 6 * screenPixelRatio) {
                    dataset[0].css.width = Math.min(elapsed * 2 + 4, 6) * screenPixelRatio;
                    dataset[0].css.shadow.shadowColor = "rgba(0, 0, 0, 0.25)";
                    dataset[0].css.shadow.blur =
                        dataset[0].css.shadow.shadowOffsetX =
                        dataset[0].css.shadow.shadowOffsetY =
                            Math.min(elapsed * 5, 5);
                    // }
                    dataset[0].points = assetMix.map(createPoint(colour, true));

                    // if (dataset[1].css.width !== 6 * screenPixelRatio) {
                    dataset[1].css.width = Math.min(elapsed * 2 + 4, 6) * screenPixelRatio;
                    dataset[1].css.shadow.shadowColor = "rgba(0, 0, 0, 0.25)";
                    dataset[1].css.shadow.blur =
                        dataset[1].css.shadow.shadowOffsetX =
                        dataset[1].css.shadow.shadowOffsetY =
                            Math.min(elapsed * 5, 5);
                    // }
                    colour = dataset[1].css.colour;
                    assetMix = dataset[1].name === firstAssetMixName ? assetMixOne : assetMixTwo;
                    dataset[1].points = assetMix.map(createPoint(colour, true));
                } else {
                    if (dataset[0].css.width > 4 * screenPixelRatio) {
                        dataset[0].css.width = Math.max(6 - 2 * elapsed, 4) * screenPixelRatio;
                        dataset[0].css.shadow.shadowColor = "rgba(0, 0, 0, 0.25)";
                        dataset[0].css.shadow.blur =
                            dataset[0].css.shadow.shadowOffsetX =
                            dataset[0].css.shadow.shadowOffsetY =
                                Math.max(5 - elapsed * 5, 0);
                    }

                    dataset[0].points = assetMix.map(createPoint(colour, dataset.length === 4 ? true : false));

                    if (dataset[1].css.width !== 6 * screenPixelRatio) {
                        dataset[1].css.width = Math.min(elapsed * 2 + 4, 6) * screenPixelRatio;
                        dataset[1].css.shadow.shadowColor = "rgba(0, 0, 0, 0.25)";
                        dataset[1].css.shadow.blur =
                            dataset[1].css.shadow.shadowOffsetX =
                            dataset[1].css.shadow.shadowOffsetY =
                                Math.min(elapsed * 5, 5);
                        assetMix = dataset[1].name === firstAssetMixName ? assetMixOne : assetMixTwo;
                        colour = dataset[1].css.colour;
                        dataset[1].points = assetMix.map(createPoint(colour, true));
                    }
                }
            } else {
                if (dataset[0].css.width !== 6 * screenPixelRatio) {
                    dataset[0].css.width = Math.min(elapsed * 2 + 4, 6) * screenPixelRatio;
                    dataset[0].css.shadow.shadowColor = "rgba(0, 0, 0, 0.25)";
                    dataset[0].css.shadow.blur =
                        dataset[0].css.shadow.shadowOffsetX =
                        dataset[0].css.shadow.shadowOffsetY =
                            Math.min(elapsed * 5, 5);
                    dataset[0].points = assetMix.map(createPoint(colour, true));
                }
            }

            InvestmentGrowthGraphGlobal.options.accessibilityTable = false;
            InvestmentGrowthGraphGlobal.options.pixelRatio = devicePixelRatio;

            canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
            drawGraph(canvas, InvestmentGrowthGraphGlobal.options, InvestmentGrowthGraphGlobal.css, dataset);
            InvestmentGrowthGraphGlobal.options.accessibilityTable = true;

            if (elapsed < 1) {
                InvestmentGrowthGraphGlobal.rafId = requestAnimationFrame(hoverLine);
            } else {
                dataset[dataset.length - 5].css.width = 6 * screenPixelRatio;
                InvestmentGrowthGraphGlobal.dataset = dataset;
                InvestmentGrowthGraphGlobal.rafId = null;
            }
        };

        InvestmentGrowthGraphGlobal.rafId = requestAnimationFrame(hoverLine);
    }

    function drawTicks(context, x, y) {
        const yAxisBottomYCoords = canvas.height / screenPixelRatio - css.canvasCss.padding.bottom;

        if (y < yAxisBottomYCoords) {
            context.save();
            context.beginPath();
            y = Math.round(y);
            context.moveTo(x, y);
            context.lineTo(x + 10, y);
            context.strokeStyle = grey;
            context.stroke();
            context.restore();
        }
    }

    let yearIncrements = 5;

    const ticks = {
        xAxis: 14,
        yAxis: 8
    };
    if (screenWidth === "medium") {
        ticks.xAxis = ticks.xAxis / 2;
        yearIncrements = 10;
    } else if (screenWidth === "small") {
        ticks.xAxis = Math.ceil(ticks.xAxis / 3);
        yearIncrements = 10;
    }

    const { xAxis, yAxis } = getInvestmentGrowthGraphAxesLimits({
        assetMixOne,
        assetMixTwo,
        canvas,
        css,
        screenPixelRatio,
        ticks
    });

    const yAxisRemoveTicks = [0];
    let xAxisRemoveTicks = [];
    for (let i = 0; i < assetMixOne.length; i++) {
        let [month, year] = assetMixOne[i].date.split("/").map((val) => Number(val));

        if (year === 1980 && month === 1) {
            continue;
        }

        if (year % yearIncrements !== 0 || month !== 6 || year === 1980) {
            xAxisRemoveTicks.push(i);
        }
    }

    const axisOptions = {
        xAxis: {
            name: "Date",
            drawAxisLine: true,
            drawGridLines: false,
            range: [xAxis.min, xAxis.max],
            removeTicks: xAxisRemoveTicks,
            ticks: assetMixOne.length
        },
        yAxis: {
            name: "",
            drawAxisLine: true,
            drawGridLines: false,
            drawTicks,
            range: [yAxis.min, yAxis.max],
            removeTicks: yAxisRemoveTicks,
            ticks: ticks.yAxis
        }
    };

    const ctx = canvas.getContext("2d");

    function hoverOnFunction(dataName, x, y, xPos, yPos, xValOnGraph, yValOnGraph, isLine) {
        $(popupOne).css("visibility", "hidden");
        $(popupStacked).css("visibility", "hidden");
        $(popupDashedLine).css("visibility", "hidden");

        function showPopup(popup) {
            function getMaxPortfolioValue(max, { date, portfolioValue }) {
                const year = Number(date.split("/")[1]);
                return year >= lowerBoundYear && year <= upperBoundYear ? Math.max(max, portfolioValue) : max;
            }

            const xOffset = $(popup).outerWidth() / 2;
            const popupLeftXCoords = xValOnGraph - css.canvasCss.padding.left / screenPixelRatio - xOffset;
            const popupRightXCoords = xValOnGraph - css.canvasCss.padding.left / screenPixelRatio + xOffset;
            const xAxisLength =
                (canvas.width - css.canvasCss.padding.left + css.canvasCss.padding.right) / screenPixelRatio;
            const xAxisRange = xAxis.max - xAxis.min;
            const xLowerBound = Math.max(
                xAxis.min,
                Math.floor(xAxis.min + (popupLeftXCoords / xAxisLength) * xAxisRange)
            );
            const xUpperBound = Math.min(
                xAxis.max,
                Math.ceil(xAxis.min + (popupRightXCoords / xAxisLength) * xAxisRange)
            );

            const lowerBoundYear = getYearFromTimestamp(xLowerBound);
            const upperBoundYear = getYearFromTimestamp(xUpperBound);

            let maxPortfolioValue = assetMixOne.reduce(getMaxPortfolioValue, Number.NEGATIVE_INFINITY);
            if (assetMixTwo) {
                const assetMixTwoMaxPortfolioValue = assetMixTwo.reduce(getMaxPortfolioValue, Number.NEGATIVE_INFINITY);
                maxPortfolioValue = Math.max(maxPortfolioValue, assetMixTwoMaxPortfolioValue);
            }

            const yOffset = $(popup).outerHeight();
            const popupBottomYCoords = yValOnGraph - 25;
            const yAxisLength =
                (canvas.height - css.canvasCss.padding.top - css.canvasCss.padding.bottom) / screenPixelRatio;
            const yAxisRange = yAxis.max - yAxis.min;
            const maxPortfolioValueYCoords =
                css.canvasCss.padding.top / screenPixelRatio +
                (yAxisLength - ((maxPortfolioValue - yAxis.min) / yAxisRange) * yAxisLength);

            $(popup).css(
                "left",
                xValOnGraph - xOffset + $(popup)[0].clientWidth > canvas.clientWidth
                    ? `${canvas.clientWidth - $(popup)[0].clientWidth}px`
                    : `${xValOnGraph - xOffset}px`
            );

            const requiresReversePopup = [
                "80 % Revenu fixe / 20 % Actions",
                "100 % Revenu fixe",
                "80% Fixed Income / 20% Equities",
                "100% Fixed Income"
            ];

            if (requiresReversePopup.includes(dataName) && xPos > xAxisLength / 2) {
                $(popup).css("top", `${yValOnGraph + 25}px`);
                $(popup).addClass("popup-reverse");
            } else if (popupBottomYCoords >= maxPortfolioValueYCoords) {
                $(popup).css("top", `${maxPortfolioValueYCoords - yOffset - 10}px`);
                $(popup).removeClass("popup-reverse");
            } else {
                $(popup).css("top", `${yValOnGraph - yOffset - 25}px`);
                $(popup).removeClass("popup-reverse");
            }
            $(popup).css("visibility", "visible");
        }

        if (InvestmentGrowthGraphGlobal.enableHover) {
            let oldDataSet = InvestmentGrowthGraphGlobal.dataset;
            let newDataSet = JSON.parse(JSON.stringify(oldDataSet));
            if (isLine) {
                hover(newDataSet, dataName);
            } else {
                if (dataName.indexOf("Dashed-Line") > -1) {
                    let idxOne = 0;
                    let idxTwo = 1;
                    const yearDifference = getYearFromTimestamp(x) - getYearFromTimestamp(newDataSet[0].points[0].x);

                    let colours = [newDataSet[0].css.colour];
                    if (newDataSet[1].type === "line") {
                        colours.push(newDataSet[1].css.colour);
                        if (
                            newDataSet[1].points[newDataSet[1].points.length - 1].y >
                            newDataSet[0].points[newDataSet[0].points.length - 1].y
                        ) {
                            colours = [colours[1], colours[0]];
                            idxOne = 1;
                            idxTwo = 0;
                        }
                    }

                    let datasetValue = newDataSet[idxOne].points.filter((point) => point.x === x)[0].y;
                    $(popupDashedLine)
                        .find("[data-id='title']")
                        .text(`${yearDifference} ${lang === "en" ? "years" : "ans"}`);
                    $(popupDashedLine).find(".legend-one .circle").css("background-color", colours[0]);
                    $(popupDashedLine)
                        .find("[data-id='assetMixOneValue']")
                        .text(formatter.formatCurrency(Math.round(datasetValue)));

                    if (newDataSet[1].type === "line") {
                        datasetValue = newDataSet[idxTwo].points.filter((point) => point.x === x)[0].y;
                        $(popupDashedLine).find(".legend-two .circle").css("background-color", colours[1]);
                        $(popupDashedLine).find(".legend-two").show();
                        $(popupDashedLine)
                            .find("[data-id='assetMixTwoValue']")
                            .text(formatter.formatCurrency(Math.round(datasetValue)));
                    } else {
                        console.log("hide?");
                        $(popupDashedLine).find(".legend-two").hide();
                    }

                    if (dataName.indexOf("3") > -1 || dataName.indexOf("4") > -1) {
                        popupDashedLine.attr("class", "popup-left popup-dashed-line");
                        xValOnGraph -= $(popupDashedLine).outerWidth() + 30;
                    } else {
                        popupDashedLine.attr("class", "popup-right popup-dashed-line");
                    }

                    $(popupOne).css("visibility", "hidden");
                    $(popupStacked).css("visibility", "hidden");

                    $(popupDashedLine).css("left", `${xValOnGraph + 17}px`);
                    $(popupDashedLine).css("top", `${$(popupDashedLine).outerHeight() / 3}px`);
                    $(popupDashedLine).css("visibility", "visible");
                } else {
                    const stackedPoints =
                        oldDataSet.length === 6
                            ? getStackedPoints({ global: InvestmentGrowthGraphGlobal, x })
                            : undefined;
                    const popup = stackedPoints === undefined ? popupOne : popupStacked;

                    if (stackedPoints) {
                        updateContent({
                            dataName: stackedPoints[0].name,
                            popup,
                            x: stackedPoints[0].x,
                            dataName2:
                                stackedPoints[0].name === firstAssetMixName ? secondAssetMixName : firstAssetMixName,
                            x2: stackedPoints[1] ? stackedPoints[1].x : stackedPoints[0].x
                        });

                        hover(newDataSet, dataName, true);
                    } else {
                        updateContent({ dataName, popup, x });
                        hover(newDataSet, dataName);
                    }

                    showPopup(popup);
                }
            }
        }
    }

    function hoverOffFunction(x, y) {
        if (InvestmentGrowthGraphGlobal.enableHover) {
            let dataset = JSON.parse(JSON.stringify(InvestmentGrowthGraphGlobal.dataset));

            for (const k in InvestmentGrowthGraphGlobal.legendPaths) {
                const path = InvestmentGrowthGraphGlobal.legendPaths[k];

                if (ctx.isPointInPath(path, x * screenPixelRatio, y * screenPixelRatio)) {
                    const dataName = dataset.filter((set) => set.css.colour === k)[0].name;

                    hover(dataset, dataName);

                    $(popupOne).css("visibility", "hidden");
                    $(popupDashedLine).css("visibility", "hidden");
                    $(popupStacked).css("visibility", "hidden");

                    return;
                }
            }

            if (dataset[dataset.length - 5].css.width !== 4 * screenPixelRatio && !InvestmentGrowthGraphGlobal.rafId2) {
                let initialTime = null;

                const hoverOff = (time) => {
                    if (!initialTime) initialTime = time;
                    const elapsed = (time - initialTime) / 200;

                    if (dataset[0].css.width !== 4 * screenPixelRatio) {
                        dataset[0].css.width = Math.max(6 - 2 * elapsed, 4) * screenPixelRatio;
                        dataset[0].css.shadow = {
                            blur: Math.max(5 - 5 * elapsed, 0),
                            shadowColour: "rgba(0, 0, 0, 0.25)",
                            shadowOffsetX: Math.max(5 - 5 * elapsed, 0),
                            shadowOffsetY: Math.max(5 - 5 * elapsed, 0)
                        };
                    }
                    let assetMix = dataset[0].name === firstAssetMixName ? assetMixOne : assetMixTwo;
                    let colour = dataset[0].css.colour;
                    dataset[0].points = assetMix.map(createPoint(colour, false));

                    if (dataset[1].type !== "dashedLine") {
                        dataset[1].css.width = Math.max(6 - 2 * elapsed, 4) * screenPixelRatio;
                        dataset[1].css.shadow = {
                            blur: Math.max(5 - 5 * elapsed, 0),
                            shadowColour: "rgba(0, 0, 0, 0.25)",
                            shadowOffsetX: Math.max(5 - 5 * elapsed, 0),
                            shadowOffsetY: Math.max(5 - 5 * elapsed, 0)
                        };
                        assetMix = dataset[1].name === firstAssetMixName ? assetMixOne : assetMixTwo;
                        colour = dataset[1].css.colour;
                        dataset[1].points = assetMix.map(createPoint(colour, false));
                    }

                    InvestmentGrowthGraphGlobal.options.accessibilityTable = false;
                    InvestmentGrowthGraphGlobal.options.pixelRatio = devicePixelRatio;

                    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
                    drawGraph(canvas, InvestmentGrowthGraphGlobal.options, InvestmentGrowthGraphGlobal.css, dataset);
                    InvestmentGrowthGraphGlobal.options.accessibilityTable = true;

                    if (elapsed < 1) {
                        InvestmentGrowthGraphGlobal.rafId2 = requestAnimationFrame(hoverOff);
                    } else {
                        dataset[dataset.length - 5].css.width = 4 * screenPixelRatio;
                        InvestmentGrowthGraphGlobal.dataset = dataset;
                        InvestmentGrowthGraphGlobal.rafId2 = null;
                    }
                };

                InvestmentGrowthGraphGlobal.rafId2 = requestAnimationFrame(hoverOff);
            }
            $(popupOne).css("visibility", "hidden");
            $(popupDashedLine).css("visibility", "hidden");
            $(popupStacked).css("visibility", "hidden");
        }
    }

    const events = {
        hoverOn: hoverOnFunction,
        hoverOff: hoverOffFunction,
        clickOn: hoverOnFunction,
        clickOff: hoverOffFunction
    };

    const titles = {
        en: "Investment Growth",
        fr: "Croissance des placements"
    };

    return {
        accessibilityTable: false,
        autoResize: false,
        axisOptions,
        events,
        pixelRatio: screenPixelRatio,
        title: titles[lang].split(" ").join("-")
    };
}

function getStackedPoints({ global, x }) {
    return global.stackedPoints.filter(
        (points) => points.filter((point) => (x % 1 !== 0 ? point.x % 1 !== 0 : point.x === x)).length !== 0
    )[0];
}

function getTimeToRecoverGraphCSS({ screenPixelRatio, screenWidth }) {
    const title = {
        font: "Roobert",
        weight: "normal",
        size: 16 * screenPixelRatio,
        colour: grey,
        rotateYLabel: true,
        xLabelTextAlign: "center",
        xLabelTextVerticalAlign: "right",
        yLabelTextAlign: "center",
        yLabelTextHorizontalAlign: "center"
    };
    const numbers = {
        xFormat: (number) => {
            if (number > 14) {
                number = (number - 3) / monthsInAYear;
            }

            return `${number}`;
        },
        xFormatForAccTable: (number) => number,
        yFormat: (number) => {
            if (screenWidth === "medium") {
                number = formatLargeCurrency(number);
            } else {
                number = formatter.formatCurrency(number, 0);
            }

            return number;
        },
        formatYValuesForAccTable: (number) => formatter.formatCurrency(number, 0),
        font: "Roobert",
        size: 15 * screenPixelRatio,
        padding: {
            xAxis: 10 * screenPixelRatio,
            yAxis: 10 * screenPixelRatio
        },
        xAxisWeight: "normal",
        yAxisWeight: "normal",
        colour: grey
    };
    const axisCss = {
        axisLineWidth: 1 * screenPixelRatio,
        axisLineColour: grey,
        gridLineWidth: 1 * screenPixelRatio,
        gridLineColour: grey,
        title,
        numbers,
        noGraphOutsideAxis: {
            y: true
        }
    };

    const left = screenWidth === "medium" ? 65 : 106;
    const padding = {
        left: left * screenPixelRatio,
        right: 15 * screenPixelRatio,
        top: 20 * screenPixelRatio,
        bottom: 50 * screenPixelRatio
    };
    const canvasCss = { padding, colour: "white" };

    const titleCss = {
        font: "Roobert",
        weight: "bold",
        size: 20 * screenPixelRatio,
        colour: "rgba(0, 0, 0, 0)",
        textAlign: "center"
    };

    return {
        axisCss,
        canvasCss,
        titleCss
    };
}

function getTimeToRecoverGraphDataset({
    assetMixOne,
    assetMixTwo,
    canvas,
    drawCallback,
    investmentAmount,
    lineColourOne,
    lineColourTwo,
    page,
    screenPixelRatio,
    showPoints,
    timePeriod
}) {
    function createPoint(lineColour, isHovered) {
        return (data) => {
            let { month, portfolioValue } = data;

            return new Point(month, portfolioValue, drawCallback({ ...data, isHovered, lineColour, screenPixelRatio }));
        };
    }

    const assetMixNames = document.querySelectorAll(".best-worst-label");
    const pointsSize = 6 * screenPixelRatio;
    let dataset = [
        {
            css: {
                width: 1 * screenPixelRatio,
                colour: grey
            },
            dashLength: 6,
            dashSpaceLength: 8,
            isHorizontal: true,
            points: assetMixOne.map(({ month }) => new Point(month, investmentAmount)),
            name: "Investment Amount",
            type: "dashedLine"
        },
        {
            css: {
                width: 6 * screenPixelRatio,
                colour: lineColourOne,
                lineCap: "round",
                lineJoin: "round",
                shadow: {
                    blur: 0,
                    shadowColor: "rgba(0, 0, 0, 0.25)",
                    shadowOffsetX: 0,
                    shadowOffsetY: 0
                },
                points: {
                    colour: white,
                    showPoints: showPoints(assetMixOne),
                    size: pointsSize,
                    style: "circle"
                }
            },
            points: assetMixOne.map(createPoint(lineColourOne)),
            name: assetMixNames[0].innerText,
            type: "line",
            enableLineHover: true,
            animate: TimeToRecoverGraphGlobal.previousColours.indexOf(lineColourOne) === -1
        }
    ];

    if (assetMixTwo) {
        dataset = dataset.concat({
            css: {
                width: 6 * screenPixelRatio,
                colour: lineColourTwo,
                lineCap: "round",
                lineJoin: "round",
                points: {
                    colour: white,
                    showPoints: showPoints(assetMixTwo),
                    size: pointsSize,
                    style: "circle"
                },
                shadow: {
                    blur: 0,
                    shadowColor: "rgba(0, 0, 0, 0.25)",
                    shadowOffsetX: 0,
                    shadowOffsetY: 0
                }
            },
            points: assetMixTwo.map(createPoint(lineColourTwo)),
            name: assetMixNames[1].innerText,
            type: "line",
            enableLineHover: true,
            animate: TimeToRecoverGraphGlobal.previousColours.indexOf(lineColourTwo) === -1
        });

        if (page) {
            const { yAxis } = getTimeToRecoverGraphAxesLimits({
                assetMixOne,
                assetMixTwo,
                canvas,
                css: TimeToRecoverGraphGlobal.css,
                ticks: {
                    xAxis: timePeriod * 12 + 4,
                    yAxis: 5
                }
            });

            const xAxisLength =
                canvas.width -
                (TimeToRecoverGraphGlobal.css.canvasCss.padding.left / screenPixelRatio) * devicePixelRatio -
                (TimeToRecoverGraphGlobal.css.canvasCss.padding.right / screenPixelRatio) * devicePixelRatio;
            const yAxisLength =
                canvas.height -
                (TimeToRecoverGraphGlobal.css.canvasCss.padding.top / screenPixelRatio) * devicePixelRatio -
                (TimeToRecoverGraphGlobal.css.canvasCss.padding.bottom / screenPixelRatio) * devicePixelRatio;

            const yAxisScale = yAxisLength / (yAxis.max - yAxis.min);
            const xAxisScale = xAxisLength / dataset[0].points.length;

            const mixOneIndices = showPoints(assetMixOne);
            const mixTwoIndices = showPoints(assetMixTwo);

            const mixOnePoints = dataset[1].points;
            const mixTwoPoints = dataset[2].points;

            const mixOnePointSize = dataset[1].css.points.size + 2 * devicePixelRatio;
            const mixTwoPointSize = dataset[2].css.points.size + 2 * devicePixelRatio;

            const distanceBetweenPoints =
                ((mixOnePointSize + mixTwoPointSize) / 2) * ($(window).outerWidth() < 576 ? 0.88 : 0.8);

            const mergedPoints = [];
            const stackedPoints = [];

            for (let i = 0; i < mixOneIndices.length; i++) {
                for (let j = 0; j < mixTwoIndices.length; j++) {
                    const xValueOne = mixOneIndices[i] * xAxisScale;
                    const xValueTwo = mixTwoIndices[j] * xAxisScale;

                    const yValueOne = mixOnePoints[mixOneIndices[i]].y * yAxisScale;
                    const yValueTwo = mixTwoPoints[mixTwoIndices[j]].y * yAxisScale;

                    if (
                        Math.abs(xValueTwo - xValueOne) < distanceBetweenPoints &&
                        Math.abs(yValueTwo - yValueOne) < distanceBetweenPoints
                    ) {
                        const stackedPointsObject = [];

                        if (mergedPoints.indexOf(mixOneIndices[i]) === -1) {
                            mergedPoints.push(mixOneIndices[i]);
                            stackedPointsObject.push({
                                x: mixOnePoints[mixOneIndices[i]].x,
                                y: mixOnePoints[mixOneIndices[i]].y,
                                name: dataset[1].name
                            });
                        }

                        if (mergedPoints.indexOf(mixTwoIndices[j]) === -1) {
                            mergedPoints.push(mixTwoIndices[j]);
                            stackedPointsObject.push({
                                x: mixTwoPoints[mixTwoIndices[j]].x,
                                y: mixTwoPoints[mixTwoIndices[j]].y,
                                name: dataset[2].name
                            });
                        }

                        if (stackedPointsObject.length !== 0) {
                            stackedPoints.push(stackedPointsObject);
                        }
                    }
                }
            }

            if (mergedPoints.length !== 0) {
                dataset[1].css.points.showPoints = mixOneIndices.filter((i) => mergedPoints.indexOf(i) === -1);
            }

            TimeToRecoverGraphGlobal.stackedPoints = stackedPoints;
        }
    }

    if (TimeToRecoverGraphGlobal.initialDraw) {
        const oldDataset = JSON.parse(JSON.stringify(dataset));
        oldDataset.forEach((dataset) => {
            if (dataset.type !== "dashedLine" && dataset.animate) {
                dataset.points.forEach((point) => {
                    dataset.points = [new Point(point.x, 0)];
                });
            }
        });
        TimeToRecoverGraphGlobal.oldDataset = oldDataset;
    }

    return dataset;
}

function getTimeToRecoverGraphAxesLimits({
    assetMixOne,
    assetMixTwo,
    canvas,
    css: {
        canvasCss: {
            padding: { top, bottom }
        }
    },
    screenPixelRatio,
    ticks
}) {
    function isLegendOverlappingLines({ yAxisMax }) {
        const legendTextFontSize = 15 * screenPixelRatio;
        const { portfolioValue: assetMixOnePortfolioValue } = assetMixOne.slice(-1)[0];

        let maxPortfolioValue = assetMixOnePortfolioValue;
        let legendBottomYOffset = legendTextFontSize;
        if (assetMixTwo) {
            const { portfolioValue: assetMixTwoPortfolioValue } = assetMixTwo.slice(-1)[0];
            maxPortfolioValue = Math.max(maxPortfolioValue, assetMixTwoPortfolioValue);
            legendBottomYOffset = legendBottomYOffset * 2 + 10 * screenPixelRatio;
        }

        const maxPortfolioValueYCoords = (maxPortfolioValue / yAxisMax) * (canvas.height - top - bottom) + bottom;
        const legendBottomYCoords = canvas.height - legendBottomYOffset;

        return legendBottomYCoords < maxPortfolioValueYCoords;
    }

    const xAxisMin = assetMixOne.reduce((min, { month }) => Math.min(min, month), Number.POSITIVE_INFINITY);
    const xAxisMax = assetMixOne.reduce((max, { month }) => Math.max(max, month), Number.NEGATIVE_INFINITY);

    let yAxisMin = assetMixOne.reduce(
        (min, { portfolioValue }) => Math.min(min, portfolioValue),
        Number.POSITIVE_INFINITY
    );
    let yAxisMax = assetMixOne.reduce(
        (max, { portfolioValue }) => Math.max(max, portfolioValue),
        Number.NEGATIVE_INFINITY
    );

    if (assetMixTwo) {
        const yAxisMinTwo = assetMixTwo.reduce(
            (min, { portfolioValue }) => Math.min(min, portfolioValue),
            Number.POSITIVE_INFINITY
        );
        const yAxisMaxTwo = assetMixTwo.reduce(
            (max, { portfolioValue }) => Math.max(max, portfolioValue),
            Number.NEGATIVE_INFINITY
        );
        yAxisMin = Math.min(yAxisMin, yAxisMinTwo);
        yAxisMax = Math.max(yAxisMax, yAxisMaxTwo);
    }

    const factor = ticks.yAxis - 1;
    yAxisMin = roundToNiceNumber(yAxisMin, 10, "floor");
    yAxisMax = roundToNiceNumber(yAxisMax, factor);

    if (isLegendOverlappingLines({ yAxisMax })) {
        yAxisMax = roundToNiceNumber(yAxisMax + 1, factor);
    }

    return {
        xAxis: {
            max: xAxisMax,
            min: xAxisMin
        },
        yAxis: {
            max: yAxisMax,
            min: yAxisMin
        }
    };
}

function getTimeToRecoverGraphOptions({
    assetMixOne,
    assetMixTwo,
    canvas,
    css,
    popupOne,
    popupStacked,
    updateContent,
    screenPixelRatio,
    screenWidth,
    timePeriod
}) {
    function createPoint(lineColour, isHovered) {
        return (data) => {
            let { month, portfolioValue } = data;

            return new Point(
                month,
                portfolioValue,
                TimeToRecoverGraphGlobal.drawCallback({ ...data, isHovered, lineColour, screenPixelRatio })
            );
        };
    }

    function drawTicks(context, x, y) {
        const yAxisBottomYCoords = canvas.height / screenPixelRatio - css.canvasCss.padding.bottom;

        if (y < yAxisBottomYCoords) {
            context.save();
            context.beginPath();
            y = Math.round(y);
            context.moveTo(x, y);
            context.lineTo(x + 10, y);
            context.strokeStyle = grey;
            context.stroke();
            context.restore();
        }
    }

    const labels = document.querySelectorAll(".best-worst-label");
    const firstAssetMixName = labels[0].innerText;
    let secondAssetMixName = undefined;
    if (labels[1]) {
        secondAssetMixName = labels[1].innerText;
    }

    function hover(dataset, dataName, both = false) {
        if (TimeToRecoverGraphGlobal.rafId && !both) {
            return;
        }

        if (dataset.length === 3 && dataset[1].name === dataName) {
            dataset = [dataset[0], dataset[2], dataset[1]];
        }

        if (!both) {
            if (dataset.length === 3) {
                if (dataset[2].css.width === 9 * screenPixelRatio) {
                    return;
                }
            } else {
                if (dataset[1].css.width === 9 * screenPixelRatio) {
                    return;
                }
            }
        } else {
            if (dataset[1].css.width === 9 * screenPixelRatio && dataset[2].css.width === 9 * screenPixelRatio) {
                return;
            }
        }
        let initialTime = null;

        const hoverLine = (time) => {
            if (!initialTime) initialTime = time;
            const elapsed = (time - initialTime) / 200;

            let assetMix = dataset[1].name === firstAssetMixName ? assetMixOne : assetMixTwo;
            let colour = dataset[1].css.colour;

            if (dataset.length === 3) {
                if (both) {
                    // if (dataset[0].css.width !== 6 * screenPixelRatio) {
                    dataset[1].css.width = Math.min(elapsed * 3 + 6, 9) * screenPixelRatio;
                    dataset[1].css.shadow.shadowColor = "rgba(0, 0, 0, 0.25)";
                    dataset[1].css.shadow.blur =
                        dataset[1].css.shadow.shadowOffsetX =
                        dataset[1].css.shadow.shadowOffsetY =
                            Math.min(elapsed * 5, 5);
                    // }
                    dataset[1].points = assetMix.map(createPoint(colour, true));

                    // if (dataset[1].css.width !== 6 * screenPixelRatio) {
                    dataset[2].css.width = Math.min(elapsed * 3 + 6, 9) * screenPixelRatio;
                    dataset[2].css.shadow.shadowColor = "rgba(0, 0, 0, 0.25)";
                    dataset[2].css.shadow.blur =
                        dataset[1].css.shadow.shadowOffsetX =
                        dataset[1].css.shadow.shadowOffsetY =
                            Math.min(elapsed * 5, 5);
                    // }
                    colour = dataset[2].css.colour;
                    assetMix = dataset[2].name === firstAssetMixName ? assetMixOne : assetMixTwo;
                    dataset[2].points = assetMix.map(createPoint(colour, true));
                } else {
                    if (dataset[1].css.width > 6 * screenPixelRatio) {
                        dataset[1].css.width = Math.max(9 - 3 * elapsed, 6) * screenPixelRatio;
                        dataset[1].css.shadow.shadowColor = "rgba(0, 0, 0, 0.25)";
                        dataset[1].css.shadow.blur =
                            dataset[1].css.shadow.shadowOffsetX =
                            dataset[1].css.shadow.shadowOffsetY =
                                Math.max(5 - elapsed * 5, 0);
                    }

                    dataset[1].points = assetMix.map(createPoint(colour, dataset.length === 4 ? true : false));

                    if (dataset[2].css.width !== 9 * screenPixelRatio) {
                        dataset[2].css.width = Math.min(elapsed * 3 + 6, 9) * screenPixelRatio;
                        dataset[2].css.shadow.shadowColor = "rgba(0, 0, 0, 0.25)";
                        dataset[2].css.shadow.blur =
                            dataset[2].css.shadow.shadowOffsetX =
                            dataset[2].css.shadow.shadowOffsetY =
                                Math.min(elapsed * 5, 5);
                        assetMix = dataset[2].name === firstAssetMixName ? assetMixOne : assetMixTwo;
                        colour = dataset[2].css.colour;
                        dataset[2].points = assetMix.map(createPoint(colour, true));
                    }
                }
            } else {
                if (dataset[2].css.width !== 9 * screenPixelRatio) {
                    dataset[2].css.width = Math.min(elapsed * 3 + 6, 9) * screenPixelRatio;
                    dataset[2].css.shadow.shadowColor = "rgba(0, 0, 0, 0.25)";
                    dataset[2].css.shadow.blur =
                        dataset[0].css.shadow.shadowOffsetX =
                        dataset[0].css.shadow.shadowOffsetY =
                            Math.min(elapsed * 5, 5);
                    dataset[2].points = assetMix.map(createPoint(colour, true));
                }
            }

            // TimeToRecoverGraphGlobal.options.accessibilityTable = false;
            TimeToRecoverGraphGlobal.options.pixelRatio = devicePixelRatio;

            canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
            drawGraph(canvas, TimeToRecoverGraphGlobal.options, TimeToRecoverGraphGlobal.css, dataset);

            if (elapsed < 1) {
                TimeToRecoverGraphGlobal.rafId = requestAnimationFrame(hoverLine);
            } else {
                dataset[dataset.length - 1].css.width = 9 * screenPixelRatio;
                TimeToRecoverGraphGlobal.dataset = dataset;
                TimeToRecoverGraphGlobal.rafId = null;
            }
        };

        TimeToRecoverGraphGlobal.rafId = requestAnimationFrame(hoverLine);
    }

    const ticks = {
        xAxis: timePeriod * 12 + 4,
        yAxis: 5
    };
    const { xAxis, yAxis } = getTimeToRecoverGraphAxesLimits({
        assetMixOne,
        assetMixTwo,
        canvas,
        css,
        screenPixelRatio,
        ticks
    });
    const removeTicks = assetMixOne.reduce(
        (results, { month }) =>
            (month > -1 && month < 15) || (month > 14 && (month - 3) % monthsInAYear !== 0)
                ? results.concat(month)
                : results,
        []
    );

    const axisOptions = {
        xAxis: {
            name: lang === "en" ? "Years" : "Ans",
            drawAxisLine: true,
            drawGridLines: false,
            range: [xAxis.min, xAxis.max],
            removeTicks,
            ticks: ticks.xAxis
        },
        yAxis: {
            name: "",
            drawAxisLine: true,
            drawGridLines: false,
            drawTicks,
            range: [yAxis.min, yAxis.max],
            ticks: ticks.yAxis
        }
    };

    const ctx = canvas.getContext("2d");

    function hoverOnFunction(dataName, x, y, xPos, yPos, xValOnGraph, yValOnGraph, isLine) {
        function showPopup(popup) {
            function getMaxPortfolioValue(max, { month, portfolioValue }) {
                return month >= lowerBoundMonth && month <= upperBoundMonth ? Math.max(max, portfolioValue) : max;
            }

            function getMinPortfolioValue(min, { month, portfolioValue }) {
                return month >= lowerBoundMonth && month <= upperBoundMonth ? Math.min(min, portfolioValue) : min;
            }

            let xOffset = $(popup).outerWidth() / 2;
            const popupLeftXCoords = xValOnGraph - css.canvasCss.padding.left / screenPixelRatio - xOffset;
            const popupRightXCoords = xValOnGraph - css.canvasCss.padding.left / screenPixelRatio + xOffset;
            const xAxisLength =
                (canvas.width - css.canvasCss.padding.left + css.canvasCss.padding.right) / screenPixelRatio;
            const xAxisRange = xAxis.max - xAxis.min;
            const lowerBoundMonth = Math.max(
                xAxis.min,
                Math.floor(xAxis.min + (popupLeftXCoords / xAxisLength) * xAxisRange)
            );
            const upperBoundMonth = Math.min(
                xAxis.max,
                Math.ceil(xAxis.min + (popupRightXCoords / xAxisLength) * xAxisRange)
            );

            const yOffset = $(popup).outerHeight();
            const yAxisLength =
                (canvas.height - css.canvasCss.padding.top - css.canvasCss.padding.bottom) / screenPixelRatio;
            const yAxisRange = yAxis.max - yAxis.min;

            let topPosition = 0;
            $(popup).removeClass("popup popup-reverse popup-alternative");
            if (x === timePeriod * 12 + 3) {
                let minPortfolioValue = assetMixOne.reduce(getMinPortfolioValue, Number.POSITIVE_INFINITY);
                if (assetMixTwo) {
                    const assetMixTwoMinPortfolioValue = assetMixTwo.reduce(
                        getMinPortfolioValue,
                        Number.POSITIVE_INFINITY
                    );
                    minPortfolioValue = Math.min(minPortfolioValue, assetMixTwoMinPortfolioValue);
                }
                const minPortfolioValueYCoords =
                    css.canvasCss.padding.top / screenPixelRatio +
                    (yAxisLength - ((minPortfolioValue - yAxis.min) / yAxisRange) * yAxisLength);
                topPosition = minPortfolioValueYCoords + 16;

                if (screenWidth === "regular") {
                    $(popup).addClass("popup-reverse");
                } else {
                    $(popup).addClass("popup-alternative");
                    xOffset = $(popup).outerWidth() * 0.8;
                }
            } else {
                $(popup).addClass("popup");

                let maxPortfolioValue = assetMixOne.reduce(getMaxPortfolioValue, Number.NEGATIVE_INFINITY);
                if (assetMixTwo) {
                    const assetMixTwoMaxPortfolioValue = assetMixTwo.reduce(
                        getMaxPortfolioValue,
                        Number.NEGATIVE_INFINITY
                    );
                    maxPortfolioValue = Math.max(maxPortfolioValue, assetMixTwoMaxPortfolioValue);
                }
                const maxPortfolioValueYCoords =
                    css.canvasCss.padding.top / screenPixelRatio +
                    (yAxisLength - ((maxPortfolioValue - yAxis.min) / yAxisRange) * yAxisLength);
                topPosition = maxPortfolioValueYCoords - yOffset - 16;
            }

            $(popup).css("left", `${xValOnGraph - xOffset}px`);
            $(popup).css("top", `${topPosition}px`);
            $(popup).css("visibility", "visible");
        }

        let oldDataSet = JSON.parse(JSON.stringify(TimeToRecoverGraphGlobal.dataset));
        let newDataSet = JSON.parse(JSON.stringify(oldDataSet));

        $(popupOne).css("visibility", "hidden");
        $(popupStacked).css("visibility", "hidden");

        if (isLine) {
            hover(newDataSet, dataName);
        } else {
            const stackedPoints =
                oldDataSet.length === 3 ? getStackedPoints({ global: TimeToRecoverGraphGlobal, x }) : undefined;
            const popup = stackedPoints === undefined ? popupOne : popupStacked;

            if (stackedPoints) {
                updateContent({
                    dataName: stackedPoints[0].name,
                    popup,
                    x: stackedPoints[0].x,
                    dataName2: stackedPoints[0].name === firstAssetMixName ? secondAssetMixName : firstAssetMixName,
                    x2: stackedPoints[1] ? stackedPoints[1].x : stackedPoints[0].x
                });
                hover(newDataSet, dataName, true);
            } else {
                updateContent({ dataName, popup, x });
                hover(newDataSet, dataName);
            }

            showPopup(popup);
        }
    }

    function hoverOffFunction(x, y) {
        let dataset = JSON.parse(JSON.stringify(TimeToRecoverGraphGlobal.dataset));

        for (const k in TimeToRecoverGraphGlobal.legendPaths) {
            const path = TimeToRecoverGraphGlobal.legendPaths[k];

            if (ctx.isPointInPath(path, x * screenPixelRatio, y * screenPixelRatio)) {
                const dataName = dataset.filter((set) => set.css.colour === k)[0].name;

                hover(dataset, dataName);

                $(popupOne).css("visibility", "hidden");
                $(popupStacked).css("visibility", "hidden");

                return;
            }
        }

        if (dataset[dataset.length - 1].css.width !== 6 * screenPixelRatio && !TimeToRecoverGraphGlobal.rafId2) {
            let initialTime = null;

            const hoverOff = (time) => {
                if (!initialTime) initialTime = time;
                const elapsed = (time - initialTime) / 200;

                if (dataset[1].css.width !== 6 * screenPixelRatio) {
                    dataset[1].css.width = Math.max(9 - 3 * elapsed, 6) * screenPixelRatio;
                    dataset[1].css.shadow = {
                        blur: Math.max(5 - 5 * elapsed, 0),
                        shadowColour: "rgba(0, 0, 0, 0.25)",
                        shadowOffsetX: Math.max(5 - 5 * elapsed, 0),
                        shadowOffsetY: Math.max(5 - 5 * elapsed, 0)
                    };
                }
                let assetMix = dataset[1].name === firstAssetMixName ? assetMixOne : assetMixTwo;
                let colour = dataset[1].css.colour;
                dataset[1].points = assetMix.map(createPoint(colour, false));

                if (dataset[2]) {
                    dataset[2].css.width = Math.max(9 - 3 * elapsed, 6) * screenPixelRatio;
                    dataset[2].css.shadow = {
                        blur: Math.max(5 - 5 * elapsed, 0),
                        shadowColour: "rgba(0, 0, 0, 0.25)",
                        shadowOffsetX: Math.max(5 - 5 * elapsed, 0),
                        shadowOffsetY: Math.max(5 - 5 * elapsed, 0)
                    };
                    assetMix = dataset[2].name === firstAssetMixName ? assetMixOne : assetMixTwo;
                    colour = dataset[2].css.colour;
                    dataset[2].points = assetMix.map(createPoint(colour, false));
                }

                TimeToRecoverGraphGlobal.options.accessibilityTable = false;
                TimeToRecoverGraphGlobal.options.pixelRatio = devicePixelRatio;

                canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
                drawGraph(canvas, TimeToRecoverGraphGlobal.options, TimeToRecoverGraphGlobal.css, dataset);

                if (elapsed < 1) {
                    TimeToRecoverGraphGlobal.rafId2 = requestAnimationFrame(hoverOff);
                } else {
                    dataset[dataset.length - 1].css.width = 6 * screenPixelRatio;
                    TimeToRecoverGraphGlobal.dataset = dataset;
                    TimeToRecoverGraphGlobal.rafId2 = null;
                }
            };

            TimeToRecoverGraphGlobal.rafId2 = requestAnimationFrame(hoverOff);

            $(popupOne).css("visibility", "hidden");
            $(popupStacked).css("visibility", "hidden");
        }
    }

    const events = {
        hoverOn: hoverOnFunction,
        hoverOff: hoverOffFunction,
        clickOn: hoverOnFunction,
        clickOff: hoverOffFunction
    };

    const titles = {
        en: "Time to Recover",
        fr: "Du temps pour récupérer"
    };

    return {
        accessibilityTable: false,
        autoResize: false,
        axisOptions,
        events,
        pixelRatio: screenPixelRatio,
        title: titles[lang].split(" ").join("-")
    };
}

function getPositiveNegativeReturnsGraphConfig() {
    return {
        padr: 2,
        r: 90,
        startAng: 1.5 * Math.PI,
        canvasColour: "white",
        shadow: {
            shadowColour: "rgba(0, 0, 0, 0.55)"
        },
        labelOptions: {
            alwaysShow: false,
            fontColor: "transparent",
            labelFill: "transparent",
            labelStroke: "transparent"
        }
    };
}

function getPositiveNegativeReturnsGraphSlices({
    assetMix: { negativeReturns, positiveReturns },
    drawCallback,
    sliceColourOne,
    sliceColourTwo,
    sliceImageOne,
    sliceImageTwo
}) {
    const sliceOneCanvas = document.getElementById("pieCanvas1");
    const sliceTwoCanvas = document.getElementById("pieCanvas2");
    const sliceOneContext = sliceOneCanvas.getContext("2d");
    const sliceTwoContext = sliceTwoCanvas.getContext("2d");

    const positiveReturnsSliceValue = Math.round(positiveReturns.percentage);
    const negativeReturnsSliceValue = Math.round(negativeReturns.percentage);
    let positiveReturnsSlice;
    let negativeReturnsSlice;
    if (sliceImageOne && sliceImageTwo) {
        const sliceOnePattern = sliceOneContext.createPattern(sliceImageOne, "repeat");
        const sliceTwoPattern = sliceTwoContext.createPattern(sliceImageTwo, "repeat");
        positiveReturnsSlice = {
            value: positiveReturnsSliceValue,
            label: "Positive Years",
            pattern: sliceOnePattern,
            drawCallback
        };
        negativeReturnsSlice = {
            value: negativeReturnsSliceValue,
            label: "Negative Years",
            pattern: sliceTwoPattern,
            drawCallback
        };
    } else {
        positiveReturnsSlice = {
            value: positiveReturnsSliceValue,
            label: "Positive Years",
            color: sliceColourOne,
            drawCallback
        };
        negativeReturnsSlice = {
            value: negativeReturnsSliceValue,
            label: "Negative Years",
            color: sliceColourTwo,
            drawCallback
        };
    }

    let slices = [positiveReturnsSlice, negativeReturnsSlice];
    slices = slices.filter(({ value }) => value > 0);

    return slices;
}

function drawAnimatedGraph(
    ctx,
    graphAnimator,
    options,
    css,
    oldDataSet,
    newDataSet,
    global,
    removeAccessibilityTable = false
) {
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

    if (!isIE) {
        graphAnimator(options, css, oldDataSet, newDataSet);
    }

    if (global.accessibilityTable && removeAccessibilityTable) {
        const removeTable = document.getElementById(options.title + "-table-content");

        if (removeTable) {
            const tableParent = removeTable.parentElement;
            tableParent.removeChild(removeTable);
        }
        drawGraph(ctx.canvas, options, css, newDataSet, global.accessibilityTable);
    } else {
        if (!removeAccessibilityTable) {
            options.accessibilityTable = false;
        }
        drawGraph(ctx.canvas, options, css, newDataSet);
    }
    if (global !== undefined) {
        global.dataset = newDataSet;
    }
}

export {
    drawAnimatedGraph,
    getBestAndWorstGraphAxesLimits,
    getBestAndWorstGraphCSS,
    getBestAndWorstGraphDataset,
    getBestAndWorstGraphOptions,
    getInvestmentGrowthGraphCSS,
    getInvestmentGrowthGraphDataset,
    getInvestmentGrowthGraphOptions,
    getPositiveNegativeReturnsGraphConfig,
    getPositiveNegativeReturnsGraphSlices,
    getTimeToRecoverGraphCSS,
    getTimeToRecoverGraphDataset,
    getTimeToRecoverGraphOptions
};
